/**
 * Contratos de entrada (Zod) y catálogos de la operación diaria: pagos, caja,
 * cobranza, cumplimiento y notificaciones push.
 */
import { z } from 'zod';
import { keyedEnum } from '../catalogs/enum.js';
import { ALERT_STATUS } from '../catalogs/operations.js';
import { EXCESS_STRATEGIES, EXCESS_STRATEGY } from '../credit/allocation.js';
import { coordinatesSchema, cuiSchema, idSchema, isoDateSchema, moneySchema, optionalText, positiveMoneySchema, REASON_MIN_LENGTH, TEXT_MAX, clientRequestIdSchema, CAPTURED_AT_REQUIRES_REQUEST_ID, capturedAtSchema, } from './common.js';
// ── Pagos ───────────────────────────────────────────────────────────────────
/** Medios de pago aceptados. */
export const PAYMENT_METHODS = ['cash', 'bank_deposit', 'bank_transfer', 'mobile_wallet', 'check'];
/** Catálogo con clave de medios de pago: `PAYMENT_METHOD.cash`. */
export const PAYMENT_METHOD = keyedEnum(PAYMENT_METHODS);
/** Etiquetas en español de cada medio de pago. */
export const PAYMENT_METHOD_LABELS = {
    cash: 'Efectivo',
    bank_deposit: 'Depósito bancario',
    bank_transfer: 'Transferencia',
    mobile_wallet: 'Billetera móvil',
    check: 'Cheque',
};
/** Registro de un pago en caja o en campo. */
export const PostPaymentSchema = z
    .object({
    loanId: idSchema,
    amount: positiveMoneySchema,
    /** Por defecto hoy; las fechas anteriores permiten registrar después cobros hechos en campo. */
    paymentDate: isoDateSchema.optional(),
    method: z.enum(PAYMENT_METHODS),
    reference: optionalText(TEXT_MAX.reference),
    excessStrategy: z.enum(EXCESS_STRATEGIES).default(EXCESS_STRATEGY.advance_installments),
    paidByThirdParty: z
        .object({ fullName: z.string().trim().min(3).max(TEXT_MAX.name), cui: cuiSchema })
        .optional(),
    notes: optionalText(TEXT_MAX.notes),
    /** Lo genera el cliente por formulario; repetirlo devuelve el pago original. */
    idempotencyKey: idSchema,
})
    .refine((p) => p.method === PAYMENT_METHOD.cash || p.reference !== undefined, {
    message: 'Ingresa el número de boleta, transferencia o cheque',
    path: ['reference'],
});
/** Cotización de un pago: cuánto se aplicaría a cada componente. */
export const PaymentQuoteQuerySchema = z.object({
    amount: z.coerce.number().int().positive().optional(),
    date: isoDateSchema.optional(),
    excessStrategy: z.enum(EXCESS_STRATEGIES).default(EXCESS_STRATEGY.advance_installments),
});
/** Reversa de un pago (requiere motivo). */
export const ReversePaymentSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(REASON_MIN_LENGTH, `Describe el motivo de la reversa (mínimo ${String(REASON_MIN_LENGTH)} caracteres)`).max(TEXT_MAX.notes),
});
// ── Sesiones de caja ────────────────────────────────────────────────────────
/** Estados de una sesión de caja. */
export const CASH_SESSION_STATUSES = ['open', 'closed'];
/** Catálogo con clave de estados de caja: `CASH_SESSION_STATUS.open`. */
export const CASH_SESSION_STATUS = keyedEnum(CASH_SESSION_STATUSES);
/** Tipos de movimiento manual de caja (distintos de pagos y desembolsos). */
export const CASH_MOVEMENT_TYPES = ['deposit_to_bank', 'expense', 'fund_in', 'fund_out'];
/** Catálogo con clave de movimientos de caja: `CASH_MOVEMENT_TYPE.expense`. */
export const CASH_MOVEMENT_TYPE = keyedEnum(CASH_MOVEMENT_TYPES);
/** Apertura de caja con el efectivo inicial. */
export const OpenCashSessionSchema = z.object({
    openingBalance: moneySchema,
    notes: optionalText(TEXT_MAX.shortText),
});
/** Movimiento manual de caja. */
export const CashMovementSchema = z.object({
    type: z.enum(CASH_MOVEMENT_TYPES),
    amount: positiveMoneySchema,
    description: z.string().trim().min(3).max(TEXT_MAX.shortText),
    reference: optionalText(TEXT_MAX.reference),
});
/** Etiquetas en español de cada movimiento de caja. */
export const CASH_MOVEMENT_LABELS = {
    deposit_to_bank: 'Depósito a banco',
    expense: 'Gasto de caja chica',
    fund_in: 'Ingreso de fondos',
    fund_out: 'Traslado de fondos',
};
/** Cierre de caja con el efectivo contado (arqueo). */
export const CloseCashSessionSchema = z.object({
    countedCash: moneySchema,
    notes: optionalText(TEXT_MAX.notes),
});
// ── Cobranza ────────────────────────────────────────────────────────────────
/** Canales de gestión de cobro. */
export const COLLECTION_CHANNELS = ['call', 'visit', 'whatsapp', 'sms', 'letter'];
/** Catálogo con clave de canales de cobro: `COLLECTION_CHANNEL.visit`. */
export const COLLECTION_CHANNEL = keyedEnum(COLLECTION_CHANNELS);
/** Etiquetas en español de cada canal de cobro. */
export const COLLECTION_CHANNEL_LABELS = {
    call: 'Llamada',
    visit: 'Visita',
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    letter: 'Carta / notificación',
};
/** Resultados posibles de una gestión de cobro. */
export const COLLECTION_OUTCOMES = [
    'promise_to_pay',
    'paid',
    'no_answer',
    'wrong_number',
    'not_found',
    'refused',
    'hardship',
    'other',
];
/** Catálogo con clave de resultados de cobro: `COLLECTION_OUTCOME.promise_to_pay`. */
export const COLLECTION_OUTCOME = keyedEnum(COLLECTION_OUTCOMES);
/** Etiquetas en español de cada resultado de cobro. */
export const COLLECTION_OUTCOME_LABELS = {
    promise_to_pay: 'Promesa de pago',
    paid: 'Pagó',
    no_answer: 'No contesta',
    wrong_number: 'Número equivocado',
    not_found: 'No se encontró',
    refused: 'Se niega a pagar',
    hardship: 'Dificultad económica (evaluar reestructura)',
    other: 'Otro',
};
/**
 * Plazo máximo de una promesa de pago, en días desde la gestión. Una promesa
 * a meses vista saca al préstamo de la bandeja de incumplidas sin compromiso
 * real; más allá de un mes corresponde una reestructuración, no una promesa.
 */
export const PROMISE_MAX_DAYS_AHEAD = 30;
/**
 * Días naturales que se esperan para que un "Pagó" registrado en una gestión
 * aparezca como pago en caja o depósito. Pasado ese plazo sin pago, el
 * efectivo pudo quedarse en manos del gestor: el cierre nocturno avisa al
 * supervisor.
 */
export const PAID_CLAIM_GRACE_DAYS = 2;
/** Registro de una gestión de cobro; una promesa de pago exige fecha y monto. */
export const CollectionActionSchema = z
    .object({
    loanId: idSchema,
    channel: z.enum(COLLECTION_CHANNELS),
    outcome: z.enum(COLLECTION_OUTCOMES),
    promiseDate: isoDateSchema.optional(),
    promiseAmount: positiveMoneySchema.optional(),
    nextActionDate: isoDateSchema.optional(),
    notes: z.string().trim().min(3).max(TEXT_MAX.longText),
    coordinates: coordinatesSchema.optional(),
    /** Idempotencia de la captura sin conexión. */
    clientRequestId: clientRequestIdSchema,
    /** Momento de la gestión en el teléfono; las fechas se validan contra ese día. */
    capturedAt: capturedAtSchema,
})
    .refine((a) => a.outcome !== COLLECTION_OUTCOME.promise_to_pay || (a.promiseDate && a.promiseAmount), {
    message: 'Una promesa de pago necesita fecha y monto',
    path: ['promiseDate'],
})
    .refine((a) => !a.capturedAt || a.clientRequestId, {
    message: CAPTURED_AT_REQUIRES_REQUEST_ID,
    path: ['capturedAt'],
});
/** Máximo de préstamos que se asignan a un gestor en una sola operación. */
const MAX_LOANS_PER_ASSIGNMENT = 500;
/** Asignación masiva de préstamos a un gestor de cobro. */
export const AssignCollectorSchema = z.object({
    loanIds: z.array(idSchema).min(1).max(MAX_LOANS_PER_ASSIGNMENT),
    collectorId: idSchema,
});
// ── Cumplimiento ────────────────────────────────────────────────────────────
/** Resolución de una alerta de cumplimiento: descartarla o reportarla a la IVE. */
export const ResolveComplianceAlertSchema = z.object({
    resolution: z.enum([ALERT_STATUS.dismissed, ALERT_STATUS.reported]),
    notes: z.string().trim().min(REASON_MIN_LENGTH).max(TEXT_MAX.longText),
    /** Número de reporte a la IVE cuando la alerta se reportó. */
    reportReference: optionalText(TEXT_MAX.reference),
});
// ── Notificaciones / Web Push ───────────────────────────────────────────────
/** Límites de una suscripción Web Push (URL del endpoint y claves en base64url). */
const PUSH_LIMITS = {
    endpointMax: 1000,
    p256dhMin: 10,
    p256dhMax: 200,
    authMin: 8,
    authMax: 100,
};
/** Suscripción Web Push del navegador. */
export const PushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(PUSH_LIMITS.endpointMax),
    keys: z.object({
        p256dh: z.string().min(PUSH_LIMITS.p256dhMin).max(PUSH_LIMITS.p256dhMax),
        auth: z.string().min(PUSH_LIMITS.authMin).max(PUSH_LIMITS.authMax),
    }),
});
//# sourceMappingURL=operations.js.map